<template>
  <div class="nem-root">
    <div class="nem-header">
      <div class="d-flex align-center" style="gap: 12px">
        <div class="nem-title-icon"><v-icon color="white">mdi-account-cog</v-icon></div>
        <div>
          <div class="text-subtitle-1 font-weight-bold">Mitarbeiter-Einstellungen</div>
          <div class="text-caption nem-muted">{{ monthName }} · Einstellungen für den neuen Einsatzplan</div>
        </div>
      </div>
      <div class="d-flex align-center flex-wrap justify-end" style="gap: 8px">
        <v-chip small outlined :color="readyCount === employees.length ? 'green accent-3' : 'orange'">
          <v-icon left x-small>{{ readyCount === employees.length ? "mdi-check-all" : "mdi-account-clock" }}</v-icon>
          {{ readyCount }}/{{ employees.length }} Planung aktiv
        </v-chip>
        <v-btn v-if="readyCount < employees.length" x-small text color="green accent-3" class="text-none" @click="markAllReady">
          Alle aktivieren
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="nem-loading">
      <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
      <p class="mt-4 nem-muted">Lade Mitarbeiterdaten…</p>
    </div>

    <div v-else-if="employees.length" class="nem-layout">
      <aside class="nem-sidebar">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          label="Mitarbeiter suchen"
          class="nem-search ma-3"></v-text-field>

        <div class="nem-employee-list">
          <button
            v-for="employee in filteredEmployees"
            :key="employee._id"
            type="button"
            class="nem-employee"
            :class="{ active: selectedEmployee && String(selectedEmployee._id) === String(employee._id) }"
            @click="selectEmployee(employee)">
            <v-avatar size="38" class="mr-3">
              <img :src="employee.picture ? '/img/profilbilder' + employee.picture : '/img/default-avatar.png'" />
            </v-avatar>
            <div class="text-left min-width-0">
              <div class="text-body-2 font-weight-bold text-truncate">{{ employee.fullName }}</div>
              <div class="text-caption nem-muted">
                {{ effectivePercentage(employee) }}% · {{ formatEmployeeHours(employee, calculateShiftCapacity(employee)) }} Kapazität
              </div>
              <div class="nem-sidebar-states mt-1">
                <span class="nem-sidebar-state" :class="employee.planningSettings.isReadyForPlanning ? 'ready' : 'inactive'">
                  <v-icon x-small>{{ employee.planningSettings.isReadyForPlanning ? "mdi-check-circle" : "mdi-pause-circle" }}</v-icon>
                  {{ employee.planningSettings.isReadyForPlanning ? "Planung aktiv" : "Planung aus" }}
                </span>
                <span v-if="employee.specialAssignments && employee.specialAssignments.length" class="nem-sidebar-state special">
                  <v-icon x-small>mdi-star</v-icon>{{ employee.specialAssignments.length }} Spezial
                </span>
              </div>
            </div>
          </button>
          <div v-if="filteredEmployees.length === 0" class="pa-5 text-center text-caption nem-muted">Keine Mitarbeiter gefunden.</div>
        </div>

        <button type="button" class="nem-coverage" :disabled="!hasCompleteWorkHoursSummaries" @click="showCalcDialog = true">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-caption font-weight-bold">Team-Abdeckung</span>
            <span v-if="hasCompleteWorkHoursSummaries" class="text-caption font-weight-bold" :class="hourDiff >= 0 ? 'success--text' : 'error--text'">
              {{ Math.round(totalSupplyHours) }}h / {{ Math.round(totalDemandHours) }}h
            </span>
            <span v-else class="text-caption font-weight-bold nem-muted">– / {{ Math.round(totalDemandHours) }}h</span>
          </div>
          <div v-if="hasCompleteWorkHoursSummaries" class="nem-progress-wrap">
            <div class="nem-progress-bg">
              <div
                class="nem-progress-fill"
                :class="coveragePercentage >= 100 ? 'complete' : coveragePercentage >= 80 ? 'partial' : 'low'"
                :style="{ width: Math.min(coveragePercentage, 100) + '%' }"></div>
            </div>
            <strong class="text-caption ml-2" :class="coveragePercentage >= 100 ? 'success--text' : 'warning--text'">
              {{ Math.round(coveragePercentage) }}%
            </strong>
          </div>
          <div class="text-caption nem-muted mt-1">
            {{ hasCompleteWorkHoursSummaries ? "Details zur Bedarfsberechnung öffnen" : "Sollstunden werden noch berechnet" }}
          </div>
        </button>
      </aside>

      <main v-if="selectedEmployee" class="nem-main">
        <div class="nem-employee-header">
          <div class="d-flex align-center min-width-0">
            <v-avatar size="54" class="mr-3 elevation-2">
              <img :src="selectedEmployee.picture ? '/img/profilbilder' + selectedEmployee.picture : '/img/default-avatar.png'" />
            </v-avatar>
            <div class="min-width-0">
              <div class="text-h6 font-weight-bold text-truncate">{{ selectedEmployee.fullName }}</div>
              <div class="text-caption nem-muted">Planungskonfiguration für {{ monthName }}</div>
            </div>
          </div>
          <div class="d-flex align-center flex-wrap justify-end" style="gap: 8px">
            <v-chip small label :color="selectedEmployee.planningSettings.isReadyForPlanning ? 'green' : 'orange'" text-color="white">
              {{ selectedEmployee.planningSettings.isReadyForPlanning ? "Bereit für Planung" : "Nicht für Planung freigegeben" }}
            </v-chip>
            <v-chip small outlined color="blue-grey lighten-2"><v-icon left x-small>mdi-content-save-check</v-icon>Direktspeicherung</v-chip>
          </div>
        </div>

        <div class="nem-content">
          <v-alert v-if="workHoursSummaryLoading" dense text type="info" icon="mdi-calculator-variant-outline" class="mb-4">
            Sollstunden werden berechnet. Bis das Ergebnis vorliegt, werden keine Nullstunden angenommen.
          </v-alert>
          <v-alert v-else-if="workHoursSummaryWarning" dense text type="warning" icon="mdi-alert-circle-outline" class="mb-4">
            <div class="d-flex align-center flex-wrap" style="gap: 10px">
              <span class="flex-grow-1">{{ workHoursSummaryWarning }}</span>
              <v-btn small text color="warning" class="text-none" @click="retryWorkHoursSummaries">
                <v-icon left small>mdi-refresh</v-icon>Erneut berechnen
              </v-btn>
            </div>
          </v-alert>
          <v-alert
            v-if="!workHoursSummaryLoading && hasPercentageMismatch(selectedEmployee)"
            dense
            text
            type="warning"
            icon="mdi-source-branch"
            class="mb-4">
            Das Planungspensum dieses Studios beträgt <strong>{{ effectivePercentage(selectedEmployee) }}%</strong>, die geladene Stundenberechnung
            verwendet aber <strong>{{ summaryPercentage(selectedEmployee) }}%</strong>. Bitte die Sollstunden neu berechnen.
          </v-alert>
          <v-alert
            v-if="!workHoursSummaryLoading && hasZeroTargetWorkdays(selectedEmployee)"
            dense
            text
            type="warning"
            icon="mdi-calendar-alert"
            class="mb-4">
            Die {{ summaryPercentage(selectedEmployee) }}% wurden übernommen, aber für {{ monthName }} wurden keine Soll-Arbeitstage gefunden. Bitte
            Eintritt, Austritt und – bei Lernenden – die Schultage prüfen.
          </v-alert>

          <v-alert v-if="isExitInMonth(selectedEmployee)" dense text type="warning" icon="mdi-account-clock" class="mb-4">
            <strong>Austritt am {{ $moment(selectedEmployee.exitDate).format("DD.MM.YYYY") }}:</strong>
            Das Soll wird nur bis zum Austrittsdatum berücksichtigt.
            <div class="nem-exit-grid mt-3">
              <div>
                <span>Voller Monat</span><strong>{{ formatEmployeeHours(selectedEmployee, calculateFullMonthSoll(selectedEmployee)) }}</strong>
              </div>
              <div>
                <span>Bis Austritt</span><strong>{{ formatEmployeeHours(selectedEmployee, calculateCappedSoll(selectedEmployee)) }}</strong>
              </div>
              <div v-if="selectedEmployee.overTime !== undefined">
                <span>Überstundenkonto</span>
                <strong :class="Number(selectedEmployee.overTime) >= 0 ? 'green--text' : 'red--text'">
                  {{ Number(selectedEmployee.overTime) > 0 ? "+" : "" }}{{ formatHours(selectedEmployee.overTime) }}
                </strong>
              </div>
            </div>
          </v-alert>

          <planning-settings-scope :key="selectedEmployee._id" :value="selectedEmployee._planningScope || 'month'" :month="planningMonthId"
              :settings="buildMonthlyPlanningSettings(selectedEmployee)" :studio-settings="selectedEmployee.shiftPlanningSettings && selectedEmployee.shiftPlanningSettings[studioId] || {}"
              :disabled="selectedEmployee._planningSettingsSaving === true" auto-save
              @input="setEmployeePlanningScope(selectedEmployee, $event)" @adopt="adoptEmployeeStandardMonths(selectedEmployee, $event)"></planning-settings-scope>
          <v-row>
            <v-col cols="12" lg="6">
              <section class="nem-section">
                <div class="nem-section-title"><v-icon small color="green accent-3">mdi-chart-donut</v-icon>Pensum & Soll</div>
                <v-card outlined class="nem-summary-card mb-4">
                  <div>
                    <span>Planungspensum dieses Studios</span><strong>{{ effectivePercentage(selectedEmployee) }}%</strong>
                  </div>
                  <div>
                    <span>Vertragssoll</span><strong>{{ formatEmployeeHours(selectedEmployee, calculateContractTarget(selectedEmployee)) }}</strong>
                  </div>
                  <div>
                    <span>Planbare Kapazität</span
                    ><strong>{{ formatEmployeeHours(selectedEmployee, calculateShiftCapacity(selectedEmployee)) }}</strong>
                  </div>
                </v-card>

                <v-switch
                  v-model="selectedEmployee.planningSettings.isReadyForPlanning"
                  color="green accent-3"
                  inset
                  label="Für die Planung freigeben"
                  hint="Nur aktive Mitarbeiter werden dem Assistenten vorgeschlagen."
                  persistent-hint
                  @change="saveSettings(selectedEmployee)"></v-switch>

                <v-text-field
                  v-model.number="selectedEmployee.planningSettings.percentages[studioId]"
                  type="number"
                  min="0"
                  max="200"
                  outlined
                  dense
                  clearable
                  suffix="%"
                  label="Planungspensum"
                  :placeholder="`Statistik ${getStandardPercentage(selectedEmployee)}%`"
                  :hint="`Gilt nur für ${planningMonthId}; leer = Stellenprozent (Statistik) ${getStandardPercentage(selectedEmployee)}%.`"
                  persistent-hint
                  class="mt-5"
                  @change="saveSettings(selectedEmployee)"></v-text-field>

                <v-switch
                  v-model="selectedEmployee.planningSettings.offsetOvertime"
                  color="orange"
                  inset
                  label="Überstunden mit dem Soll verrechnen"
                  hint="Übernimmt beim Aktivieren den aktuellen Überstundensaldo als Soll-Korrektur."
                  persistent-hint
                  @change="toggleOvertimeOffset(selectedEmployee)"></v-switch>

                <v-expand-transition>
                  <v-text-field
                    v-if="selectedEmployee.planningSettings.offsetOvertime"
                    v-model.number="selectedEmployee.planningSettings.customSollAdjustment"
                    type="number"
                    outlined
                    dense
                    suffix="h"
                    label="Manuelle Soll-Korrektur"
                    hint="Negativ reduziert, positiv erhöht das Monatssoll."
                    persistent-hint
                    @change="saveSettings(selectedEmployee)"></v-text-field>
                </v-expand-transition>

                <v-switch
                  v-model="selectedEmployee.planningSettings.groupShifts"
                  color="green"
                  inset
                  label="Schichten möglichst in Blöcken gruppieren"
                  hint="Einsätze nach Möglichkeit an aufeinanderfolgenden Tagen planen."
                  persistent-hint
                  @change="saveSettings(selectedEmployee)"></v-switch>
              </section>

              <section class="nem-section mt-4">
                <div class="nem-section-title"><v-icon small color="blue lighten-2">mdi-school</v-icon>Schultage</div>
                <v-alert dense text type="info" class="mb-3">
                  Lernendenstatus und Schultage gehören zum Mitarbeiterprofil und werden dauerhaft gespeichert.
                </v-alert>
                <v-switch
                  v-model="selectedEmployee.isApprentice"
                  dense
                  inset
                  color="blue"
                  label="Als Lernende:r berücksichtigen"
                  @change="updateEmployeeStatus(selectedEmployee)"></v-switch>
                <v-expand-transition>
                  <div v-if="selectedEmployee.isApprentice" class="pl-1">
                    <div class="text-caption nem-muted mb-2">Schultage reduzieren das Soll ausserhalb der hinterlegten Schulferien.</div>
                    <v-chip-group
                      :value="getSchoolDaysAsArray(selectedEmployee)"
                      multiple
                      column
                      @change="(days) => setSchoolDaysFromArray(selectedEmployee, days)">
                      <v-chip v-for="day in weekdays.slice(0, 5)" :key="'school-' + day.value" small filter :value="day.value" color="blue">
                        {{ day.short }}
                      </v-chip>
                    </v-chip-group>
                    <v-switch
                      v-model="selectedEmployee.planningSettings.longCommute"
                      dense
                      color="blue"
                      label="Langer Schulweg"
                      @change="saveSettings(selectedEmployee)"></v-switch>
                  </div>
                </v-expand-transition>
              </section>

              <section class="nem-section mt-4">
                <div class="nem-section-title"><v-icon small color="blue-grey lighten-2">mdi-eye-settings</v-icon>Dauerhafte Sichtbarkeit</div>
                <div class="text-caption nem-muted mb-3">
                  Diese beiden Schalter ändern die Studio-Stammdaten und gelten nicht nur für {{ monthName }}.
                </div>
                <div class="nem-setting-row">
                  <div>
                    <div class="text-body-2 font-weight-bold">Mitarbeiter verstecken</div>
                    <div class="text-caption nem-muted">Auf Website und in allgemeinen Listen ausblenden</div>
                  </div>
                  <v-switch
                    :input-value="getStudioInfo(selectedEmployee).hide"
                    dense
                    color="orange"
                    hide-details
                    class="mt-0"
                    @change="(value) => updateStudioHideStatus(selectedEmployee, value)"></v-switch>
                </div>
                <v-expand-transition>
                  <div v-if="getStudioInfo(selectedEmployee).hide" class="nem-setting-row mt-2">
                    <div>
                      <div class="text-body-2 font-weight-bold">Im Kalender trotzdem anzeigen</div>
                      <div class="text-caption nem-muted">Weiterhin für Arbeitsplanung und Kalender verwenden</div>
                    </div>
                    <v-switch
                      :input-value="getStudioInfo(selectedEmployee).showInCalendarAnyway"
                      dense
                      color="green"
                      hide-details
                      class="mt-0"
                      @change="(value) => updateStudioShowInCalendarAnyway(selectedEmployee, value)"></v-switch>
                  </div>
                </v-expand-transition>
              </section>
            </v-col>

            <v-col cols="12" lg="6">
              <section class="nem-section">
                <div class="nem-section-title"><v-icon small color="red lighten-2">mdi-calendar-remove</v-icon>Wiederkehrende Sperrtage</div>
                <div class="text-caption nem-muted mb-2">An diesen Wochentagen wird die Person nicht automatisch vorgeschlagen.</div>
                <v-chip-group v-model="selectedEmployee.planningSettings.recurringAbsence" multiple column @change="saveSettings(selectedEmployee)">
                  <v-chip v-for="day in weekdays" :key="'blocked-' + day.value" small filter :value="day.value" color="red">{{ day.short }}</v-chip>
                </v-chip-group>
              </section>

              <section class="nem-section mt-4">
                <div class="nem-section-title"><v-icon small color="cyan lighten-2">mdi-domain-switch</v-icon>Tage in anderem Studio</div>
                <div class="text-caption nem-muted mb-2">Kennzeichnet regelmässige externe Einsätze für die automatische Planung.</div>
                <v-chip-group
                  v-model="selectedEmployee.planningSettings.recurringExternalStudio"
                  multiple
                  column
                  @change="saveSettings(selectedEmployee)">
                  <v-chip v-for="day in weekdays" :key="'external-' + day.value" small filter :value="day.value" color="cyan">{{ day.short }}</v-chip>
                </v-chip-group>
              </section>

              <section class="nem-section mt-4">
                <div class="nem-section-title"><v-icon small color="pink lighten-2">mdi-account-heart</v-icon>Team-Präferenzen</div>
                <v-autocomplete
                  v-model="selectedEmployee.planningSettings.preferredPartners"
                  :items="otherEmployees(selectedEmployee)"
                  item-text="fullName"
                  item-value="userId"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  outlined
                  dense
                  hide-details
                  label="Bevorzugte Teampartner"
                  placeholder="Mitarbeiter auswählen"
                  @change="saveSettings(selectedEmployee)"></v-autocomplete>
              </section>

              <section class="nem-section mt-4">
                <div class="nem-section-title"><v-icon small color="purple lighten-2">mdi-pin</v-icon>Fixe Schichten</div>
                <div class="text-caption nem-muted mb-3">Die Vorgabe wird bei der automatischen Zuweisung berücksichtigt.</div>
                <div v-for="day in weekdays" :key="'fixed-' + day.value" class="nem-fixed-row">
                  <span class="text-caption font-weight-bold">{{ day.short }}</span>
                  <v-select
                    v-model="selectedEmployee.planningSettings.fixedShifts[day.value]"
                    :items="availableShifts"
                    item-text="name"
                    item-value="_id"
                    dense
                    outlined
                    hide-details
                    clearable
                    placeholder="Automatisch"
                    @change="saveSettings(selectedEmployee)">
                    <template v-slot:selection="{ item }">
                      <span class="nem-color-dot" :style="{ background: item.color || '#777' }"></span>{{ item.name }}
                    </template>
                  </v-select>
                </div>
              </section>
            </v-col>
          </v-row>

          <section class="nem-section mt-4">
            <div class="d-flex align-start justify-space-between flex-wrap mb-3" style="gap: 10px">
              <div>
                <div class="nem-section-title mb-1">
                  <v-icon small color="amber lighten-2">mdi-calendar-account</v-icon>Abwesenheiten & Monatsereignisse
                </div>
                <div class="text-caption nem-muted">Nur zur Information – diese Einträge werden hier nicht verändert.</div>
              </div>
              <div class="d-flex flex-wrap justify-end" style="gap: 6px">
                <v-chip v-for="count in selectedMonthEntryCounts" :key="count.type" x-small outlined :color="count.color">
                  <v-icon left x-small>{{ count.icon }}</v-icon
                  >{{ count.label }}: {{ count.value }}
                </v-chip>
              </div>
            </div>

            <div v-if="selectedMonthEntries.length" class="nem-event-list">
              <div v-for="entry in selectedMonthEntries" :key="entry.key" class="nem-event-row">
                <v-chip small label :color="entry.color" text-color="white" class="nem-event-type">
                  <v-icon left x-small>{{ entry.icon }}</v-icon
                  >{{ entry.label }}
                </v-chip>
                <div class="nem-event-date">{{ entry.dateLabel }}</div>
                <div class="nem-event-info">
                  <div class="text-body-2 font-weight-medium">{{ entry.info }}</div>
                  <div v-if="entry.detail" class="text-caption nem-muted mt-1">{{ entry.detail }}</div>
                </div>
                <v-chip v-if="entry.statusText" x-small outlined :color="entry.statusColor || entry.color">{{ entry.statusText }}</v-chip>
              </div>
            </div>
            <div v-else class="nem-empty-events">
              <v-icon color="green accent-3" class="mr-2">mdi-calendar-check-outline</v-icon>
              Keine Ferien, Wünsche, Abwesenheiten oder Spezialzuweisungen in diesem Monat.
            </div>
          </section>
        </div>
      </main>
    </div>

    <div v-else class="nem-loading">
      <v-icon size="48" color="rgba(255,255,255,.18)">mdi-account-off-outline</v-icon>
      <p class="mt-3 nem-muted">Keine Mitarbeiter für dieses Studio gefunden.</p>
    </div>

    <v-dialog v-model="showCalcDialog" max-width="700" dark>
      <v-card class="rounded-xl">
        <v-card-title>
          Bedarfs-Berechnung
          <v-spacer></v-spacer>
          <v-btn icon @click="showCalcDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6" class="nem-calc-border">
              <div class="text-h6 mb-2">Bedarf</div>
              <div class="text-h4 font-weight-bold mb-4">{{ Math.round(totalDemandHours) }}h</div>
              <v-simple-table dense class="transparent">
                <tbody>
                  <tr>
                    <td>Standard-Monat</td>
                    <td class="text-right">{{ calcStats.standardHours.toFixed(1) }}h</td>
                  </tr>
                  <tr v-if="calcStats.removedHours > 0">
                    <td class="orange--text">Entfall durch Spezialtage</td>
                    <td class="text-right orange--text">- {{ calcStats.removedHours.toFixed(1) }}h</td>
                  </tr>
                  <tr v-if="calcStats.specialHours > 0">
                    <td class="blue--text text--lighten-2">Spezial- & Zusatzschichten</td>
                    <td class="text-right blue--text text--lighten-2">+ {{ calcStats.specialHours.toFixed(1) }}h</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-if="calcStats.specialEvents.length" class="mt-4">
                <div class="text-caption font-weight-bold nem-muted mb-2">GEPLANTE SPEZIAL-EVENTS</div>
                <div v-for="(event, index) in calcStats.specialEvents" :key="index" class="d-flex justify-space-between text-caption mb-1">
                  <span>{{ $moment(event.date).format("DD.MM") }}: {{ event.shifts }} Schichten</span>
                  <v-chip x-small label :color="event.mode === 'replace' ? 'orange' : 'blue'">{{
                    event.mode === "replace" ? "Ersatz" : "Zusatz"
                  }}</v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-h6 mb-2">Kapazität</div>
              <div class="text-h4 font-weight-bold mb-4" :class="hourDiff >= 0 ? 'success--text' : 'error--text'">
                {{ Math.round(totalSupplyHours) }}h
              </div>
              <div class="text-body-2 nem-muted mb-3">Summe aller für die Planung freigegebenen Mitarbeiter.</div>
              <v-divider class="mb-3"></v-divider>
              <div class="d-flex justify-space-between text-body-2">
                <span>Differenz:</span>
                <strong :class="hourDiff >= 0 ? 'success--text' : 'error--text'">{{ hourDiff > 0 ? "+" : "" }}{{ Math.round(hourDiff) }}h</strong>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LegacyEmployeeSettings from "./shiftManagementMainDialogCreateEmployee.vue"

export default {
  name: "ShiftManagementMainDialogCreateEmployeeModern",
  extends: LegacyEmployeeSettings,
  data() {
    return {
      search: "",
      selectedEmployeeId: null,
      weekdays: [
        { value: 1, short: "Mo" },
        { value: 2, short: "Di" },
        { value: 3, short: "Mi" },
        { value: 4, short: "Do" },
        { value: 5, short: "Fr" },
        { value: 6, short: "Sa" },
        { value: 7, short: "So" },
      ],
    }
  },
  computed: {
    filteredEmployees() {
      const needle = String(this.search || "")
        .trim()
        .toLowerCase()
      return [...(this.employees || [])]
        .filter(
          (employee) =>
            !needle ||
            String(employee.fullName || "")
              .toLowerCase()
              .includes(needle),
        )
        .sort((left, right) => String(left.fullName || "").localeCompare(String(right.fullName || ""), "de"))
    },
    selectedEmployee() {
      return (this.employees || []).find((employee) => String(employee._id) === String(this.selectedEmployeeId)) || (this.employees || [])[0] || null
    },
    selectedMonthEntries() {
      return this.buildMonthEntries(this.selectedEmployee)
    },
    canProceedToAssistant() {
      return !this.loading && this.employees.length > 0 && this.readyCount > 0 && this.hasCompleteWorkHoursSummaries
    },
    selectedMonthEntryCounts() {
      const definitions = {
        special: { label: "Spezial", color: "purple", icon: "mdi-star" },
        vacation: { label: "Ferien", color: "blue", icon: "mdi-beach" },
        freeWish: { label: "Frei", color: "red", icon: "mdi-close-circle" },
        shiftWish: { label: "Wünsche", color: "green", icon: "mdi-clock-outline" },
        absence: { label: "Abwesenheit", color: "orange", icon: "mdi-calendar-remove" },
        publicHoliday: { label: "Feiertage", color: "amber darken-2", icon: "mdi-star-four-points" },
        schoolHoliday: { label: "Schulferien", color: "teal", icon: "mdi-school-outline" },
      }
      const counts = this.selectedMonthEntries.reduce((result, entry) => {
        result[entry.type] = (result[entry.type] || 0) + 1
        return result
      }, {})
      return Object.keys(definitions)
        .filter((type) => counts[type] > 0)
        .map((type) => ({ type, value: counts[type], ...definitions[type] }))
    },
  },
  watch: {
    employees: {
      immediate: true,
      handler(employees) {
        const currentStillExists = (employees || []).some((employee) => String(employee._id) === String(this.selectedEmployeeId))
        if (!currentStillExists) this.selectedEmployeeId = employees && employees[0] ? String(employees[0]._id) : null
      },
    },
    canProceedToAssistant: {
      immediate: true,
      handler(value) {
        this.$emit("proceed-state", value === true)
      },
    },
  },
  methods: {
    selectEmployee(employee) {
      this.selectedEmployeeId = employee ? String(employee._id) : null
    },
    effectivePercentage(employee) {
      const studioInfo = this.getStudioInfo(employee)
      const value = this.getPlanningPercentage(studioInfo, employee?.planningSettings)
      return Number.isFinite(Number(value)) ? Number(value) : 0
    },
    formatHours(value) {
      if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) return "–"
      return `${Number(value).toFixed(1)}h`
    },
    hasValidEmployeeHours(employee) {
      return employee?.workHoursSummaryUnavailable !== true && this.isValidWorkHoursSummary(employee?.hoursSummary)
    },
    formatEmployeeHours(employee, value) {
      return this.hasValidEmployeeHours(employee) ? this.formatHours(value) : "–"
    },
    summaryPercentage(employee) {
      if (!this.hasValidEmployeeHours(employee)) return null
      const value = Number(employee?.hoursSummary?.contractPercentage)
      return Number.isFinite(value) ? value : null
    },
    hasPercentageMismatch(employee) {
      const calculated = this.summaryPercentage(employee)
      if (calculated === null) return false
      return Math.abs(calculated - this.effectivePercentage(employee)) > 0.01
    },
    hasZeroTargetWorkdays(employee) {
      if (!this.hasValidEmployeeHours(employee)) return false
      const percentage = this.summaryPercentage(employee)
      const targetWorkdays = Number(employee?.hoursSummary?.targetWorkdays)
      return percentage > 0 && Number.isFinite(targetWorkdays) && targetWorkdays === 0
    },
    formatShiftTimes(shift) {
      const times = shift?.times?.length ? shift.times : shift?.shiftTemplate?.times || []
      if (!times.length) return "Keine Zeit hinterlegt"
      return `${times[0].start} – ${times[times.length - 1].end}`
    },
    buildMonthEntries(employee) {
      if (!employee) return []
      const entries = []
      const add = (entry) => entries.push(entry)

      ;(employee.specialAssignments || []).forEach((shift, index) =>
        add({
          key: `special-${shift._id || index}`,
          type: "special",
          label: "Spezial",
          color: "purple",
          icon: "mdi-star",
          sortDate: shift.date,
          dateLabel: this.$moment(shift.date).format("dd, DD.MM"),
          info: shift.shiftTemplate?.name || shift.allocationNote || "Spezial-Einsatz",
          detail: this.formatShiftTimes(shift),
          statusText: "Fix zugeteilt",
        }),
      )
      ;(employee.vacations || []).forEach((vacation, index) => {
        const dates = [...(vacation.dates || [])]
          .filter((date) => date?.date)
          .sort((left, right) => String(left.date).localeCompare(String(right.date)))
        if (!dates.length) return
        add({
          key: `vacation-${vacation._id || index}`,
          type: "vacation",
          label: "Ferien",
          color: "blue",
          icon: "mdi-beach",
          sortDate: dates[0].date,
          dateLabel:
            dates.length === 1
              ? this.$moment(dates[0].date).format("dd, DD.MM")
              : `${this.$moment(dates[0].date).format("DD.MM")} – ${this.$moment(dates[dates.length - 1].date).format("DD.MM")}`,
          info: vacation.reason || vacation.note || "Genehmigte Ferien",
          statusText: "Bestätigt",
          statusColor: "green",
        })
      })
      ;(employee.freeWishes || []).forEach((wish, index) =>
        add({
          key: `free-${wish._id || index}`,
          type: "freeWish",
          label: "Frei",
          color: "red",
          icon: "mdi-close-circle",
          sortDate: wish.day,
          dateLabel: this.$moment(wish.day).format("dd, DD.MM"),
          info: wish.comment || wish.note || "Freiwunsch für den ganzen Tag",
          statusText: wish.status === "accepted" ? "Bestätigt" : "Erfasst",
          statusColor: wish.status === "accepted" ? "green" : "blue-grey",
        }),
      )
      ;(employee.shiftWishes || []).forEach((wish, index) =>
        add({
          key: `shift-${wish._id || index}`,
          type: "shiftWish",
          label: "Wunsch",
          color: "green",
          icon: "mdi-clock-outline",
          sortDate: wish.day,
          dateLabel: this.$moment(wish.day).format("dd, DD.MM"),
          info: `${this.getShiftTypeName(wish.type)}schicht`,
          detail: wish.comment || wish.note || "",
          statusText: wish.status === "accepted" ? "Bestätigt" : "Erfasst",
          statusColor: wish.status === "accepted" ? "green" : "blue-grey",
        }),
      )
      ;(employee.absences || []).forEach((absence, index) =>
        add({
          key: `absence-${absence._id || index}`,
          type: "absence",
          label: absence.label || "Abwesenheit",
          color: absence.color || "orange",
          icon: absence.icon || "mdi-calendar-remove",
          sortDate: absence.sickDays?.[0]?.date || "",
          dateLabel: absence.formattedRange || "",
          info:
            Number(absence.sickPercentage || 100) < 100
              ? `${absence.sickPercentage}% ${(absence.label || "Abwesenheit").toLowerCase()}`
              : absence.label || "Abwesenheit",
          detail: absence.note || "",
          statusText: absence.status === "accepted" ? "Bestätigt" : "Ausstehend",
          statusColor: absence.status === "accepted" ? "green" : "orange",
        }),
      )
      ;(this.publicHolidays || []).forEach((holiday, index) => {
        const date = holiday.date || holiday.startDate
        if (!date) return
        add({
          key: `public-${holiday._id || date || index}`,
          type: "publicHoliday",
          label: "Feiertag",
          color: "amber darken-2",
          icon: "mdi-star-four-points",
          sortDate: date,
          dateLabel: this.$moment(date).format("dd, DD.MM"),
          info: holiday.name || "Feiertag",
        })
      })

      if (employee.isApprentice) {
        ;(this.schoolHolidays || []).forEach((holiday, index) => {
          if (!holiday.startDate || !holiday.endDate) return
          const overlapsMonth =
            this.$moment(holiday.startDate).isSameOrBefore(this.planningEndDate, "day") &&
            this.$moment(holiday.endDate).isSameOrAfter(this.planningStartDate, "day")
          if (!overlapsMonth) return
          add({
            key: `school-${holiday._id || index}`,
            type: "schoolHoliday",
            label: "Schulferien",
            color: "teal",
            icon: "mdi-school-outline",
            sortDate: holiday.startDate,
            dateLabel: `${this.$moment(holiday.startDate).format("DD.MM")} – ${this.$moment(holiday.endDate).format("DD.MM")}`,
            info: holiday.name || "Hinterlegte Schulferien",
            detail: "Konfigurierte Schultage gelten in diesem Zeitraum als normale Arbeitstage.",
          })
        })
      }

      return entries.sort(
        (left, right) => String(left.sortDate || "").localeCompare(String(right.sortDate || "")) || left.label.localeCompare(right.label, "de"),
      )
    },
  },
}
</script>

<style scoped>
.nem-root {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #17191d;
  color: white;
}
.nem-header {
  min-height: 68px;
  flex-shrink: 0;
  padding: 10px 76px 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.28);
}
.nem-title-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #43a047, #1b5e20);
}
.nem-loading {
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.nem-layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 285px minmax(0, 1fr);
}
.nem-sidebar {
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.18);
}
.nem-search {
  flex: 0 0 auto !important;
}
.nem-employee-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 10px 12px;
}
.nem-employee {
  width: 100%;
  min-height: 62px;
  border: 0;
  border-radius: 11px;
  padding: 8px 10px;
  margin-bottom: 4px;
  color: inherit;
  background: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.nem-employee:hover {
  background: rgba(255, 255, 255, 0.05);
}
.nem-employee.active {
  background: rgba(76, 175, 80, 0.14);
  box-shadow: inset 3px 0 #69f0ae;
}
.nem-sidebar-states {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.nem-sidebar-state {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 5px;
  border-radius: 999px;
  font-size: 0.62rem;
  line-height: 1.2;
  font-weight: 700;
  white-space: nowrap;
}
.nem-sidebar-state .v-icon {
  color: inherit !important;
}
.nem-sidebar-state.ready {
  color: #69f0ae;
  background: rgba(105, 240, 174, 0.1);
}
.nem-sidebar-state.inactive {
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.06);
}
.nem-sidebar-state.special {
  color: #ce93d8;
  background: rgba(206, 147, 216, 0.1);
}
.nem-coverage {
  flex-shrink: 0;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 12px 14px;
  color: inherit;
  text-align: left;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.nem-coverage:hover {
  background: rgba(255, 255, 255, 0.04);
}
.nem-coverage:disabled {
  cursor: default;
}
.nem-coverage:disabled:hover {
  background: rgba(0, 0, 0, 0.2);
}
.nem-progress-wrap {
  display: flex;
  align-items: center;
}
.nem-progress-bg {
  flex: 1;
  height: 5px;
  overflow: hidden;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.09);
}
.nem-progress-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.4s ease;
}
.nem-progress-fill.complete {
  background: linear-gradient(90deg, #43a047, #66bb6a);
}
.nem-progress-fill.partial {
  background: linear-gradient(90deg, #fb8c00, #ffa726);
}
.nem-progress-fill.low {
  background: linear-gradient(90deg, #e53935, #ef5350);
}
.nem-main {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.nem-employee-header {
  padding: 16px 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.nem-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
}
.nem-section {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.025);
}
.nem-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 14px;
}
.nem-summary-card {
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.15) !important;
}
.nem-summary-card > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  font-size: 0.82rem;
}
.nem-summary-card span,
.nem-muted {
  color: rgba(255, 255, 255, 0.52);
}
.nem-exit-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(110px, 1fr));
  gap: 8px;
}
.nem-exit-grid > div {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.14);
}
.nem-exit-grid span {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.56);
}
.nem-exit-grid strong {
  margin-top: 2px;
}
.nem-setting-row {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 9px 11px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.12);
}
.nem-fixed-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  margin-bottom: 7px;
}
.nem-color-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 7px;
}
.nem-event-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.nem-event-row {
  min-height: 54px;
  display: grid;
  grid-template-columns: 125px 105px minmax(160px, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.13);
}
.nem-event-type {
  justify-self: start;
  min-width: 94px;
  justify-content: flex-start;
}
.nem-event-date {
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}
.nem-event-info {
  min-width: 0;
}
.nem-empty-events {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.82rem;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 11px;
}
.nem-calc-border {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}
.min-width-0 {
  min-width: 0;
}
@media (max-width: 900px) {
  .nem-layout {
    grid-template-columns: 225px minmax(0, 1fr);
  }
  .nem-employee-header {
    align-items: flex-start;
  }
  .nem-event-row {
    grid-template-columns: 110px 90px minmax(120px, 1fr);
  }
  .nem-event-row > .v-chip:last-child {
    grid-column: 3;
    justify-self: start;
  }
}
@media (max-width: 700px) {
  .nem-root {
    overflow: auto;
  }
  .nem-header,
  .nem-employee-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .nem-layout {
    display: flex;
    flex-direction: column;
    min-height: auto;
  }
  .nem-sidebar {
    min-height: 330px;
    max-height: 420px;
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  .nem-main {
    min-height: 650px;
  }
  .nem-content {
    overflow: visible;
    padding: 14px;
  }
  .nem-event-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .nem-event-row > .v-chip:last-child {
    grid-column: 1;
  }
  .nem-exit-grid {
    grid-template-columns: 1fr;
  }
  .nem-calc-border {
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-bottom: 20px;
    margin-bottom: 12px;
  }
}
</style>
